import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { GlobalFieldId, QuerySchemaRelationWrite, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FormWriteContext, RelationEditState } from '#layers/forms/utils/form-write-context';
import { computed, toValue } from 'vue';
import { usePermissionsStore } from '#layers/access/public';
import { getRelationPrimitiveFields, queryCapabilityAllows, UNSUPPORTED_CAPABILITY } from '#layers/data-model/public';
import { RelationEditKinds } from '#layers/forms/utils/form-write-context';

export interface UseRelationPermissionsOptions {
	field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField | null>;
	/** The parent form's write; relation actions ride its payload. */
	writeContext: MaybeRefOrGetter<FormWriteContext | undefined>;
}

export function useRelationPermissionsBase(options: UseRelationPermissionsOptions) {
	const permissionsStore = usePermissionsStore();

	const field = computed(() => toValue(options.field));
	const currentColId = computed(() => field.value?.collection?.id ?? null);
	const oppositeColId = computed(() => field.value?.oppositeCollection?.id ?? null);
	const writeContext = computed(() => toValue(options.writeContext));
	const isCreateForm = computed(() => writeContext.value?.isCreate ?? false);

	// Get constrained field IDs using utility
	const constraintFields = computed(() => {
		if (!field.value) return { fields: [], oppositeFields: [] };
		return getRelationPrimitiveFields(field.value);
	});

	const localConstrainedFieldIds = computed<GlobalFieldId[]>(
		() => constraintFields.value.fields.map((f) => f.id),
	);

	const oppositeConstrainedFieldIds = computed<GlobalFieldId[]>(
		() => constraintFields.value.oppositeFields.map((f) => f.id),
	);

	const relationWrite = computed(() => field.value && writeContext.value
		? writeContext.value.relation(field.value)
		: UNSUPPORTED_CAPABILITY);

	/** Whether the parent form's write accepts a nested operation on this relation. */
	function relationAllows(predicate: (relation: QuerySchemaRelationWrite) => boolean): boolean {
		return queryCapabilityAllows(relationWrite.value, predicate);
	}

	/**
	 * The contexts drawers on this relation submit through: a pending item through the parent's
	 * nested create, an existing one through its nested update. The write context checks the nested
	 * operation; these add the user's permissions.
	 */
	function useRelatedWriteContexts(canCreate: ComputedRef<boolean>) {
		const createContext = computed(() => canCreate.value && field.value
			? writeContext.value?.createRelated(field.value)
			: undefined);

		function editContext(state: RelationEditState): FormWriteContext | undefined {
			if (state.kind === RelationEditKinds.Created) return createContext.value;
			if (!field.value || !oppositeColId.value || !permissionsStore.canUpdate(oppositeColId.value)) return;
			return writeContext.value?.updateRelated(field.value, state.key);
		}

		return { createContext, editContext };
	}

	return {
		permissionsStore,
		field,
		currentColId,
		oppositeColId,
		isCreateForm,
		writeContext,
		localConstrainedFieldIds,
		oppositeConstrainedFieldIds,
		relationAllows,
		useRelatedWriteContexts,
	};
}
